import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  PageHeader, Card, Stat, Loading, ErrorState, useHorizontalScrollShadow,
} from "../components/ui";
import { fetchPrepaidReconciliation } from "../api";
import { money, MONTH_SHORT, fmtDate } from "../lib/format";

/**
 * Prepaid reconciliation.
 *
 * One column per month. Two balances face each other - the closing balance the
 * prepaid workbook says, and the closing balance the ledger says - and where
 * they agree that is the whole story, so nothing else is shown.
 *
 * A month only opens up when the two disagree. Then the ledger entries that
 * make up the difference are listed underneath that month's column and nowhere
 * else, so the eye travels straight down from the break to its cause.
 *
 * A month the workbook has not reached yet is a third state: the ledger has
 * moved on, the workbook has not, and that is expected rather than wrong.
 */

const monthLabel = (m) => {
  const x = /^(\d{4})-(\d{2})$/.exec(m);
  return x ? `${MONTH_SHORT[Number(x[2]) - 1]} ${x[1]}` : m;
};

const cash = (v) => (v == null ? <span className="muted">—</span> : money(Number(v)));

/** Split the Deltek "Supplier / narrative" string without losing either half. */
const describe = (e) => {
  const parts = String(e.description || "").split("/").map((s) => s.trim()).filter(Boolean);
  return { lead: parts[0] || "GL transaction", rest: parts.slice(1).join(" / ") };
};

/**
 * Prepaid expenses.
 *
 * Reconciling the workbook to the ledger is finance's job, not a partner's, so
 * this lives under Admin. `embedded` drops the page chrome — Admin supplies the
 * heading and the section switcher — and keeps the two facts the header carried
 * (how far each side has been posted, and a way to re-run it) on a line of
 * their own.
 */
export default function Prepaid({ embedded = false }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const scrollRef = useHorizontalScrollShadow();

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try { setData(await fetchPrepaidReconciliation()); }
    catch (err) { setError(err.message); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const months = useMemo(() => (data?.months || []).map((m) => ({
    ...m,
    // Three states, not two: agreed, genuinely out, or simply not reached yet.
    state: m.scheduleBalance == null ? "pending" : m.matches ? "ok" : "break",
  })), [data]);

  // Entries hang off the month they belong to, so one row per entry with a
  // single populated cell. Only open months contribute any.
  const entryRows = useMemo(() => {
    const out = [];
    for (const m of months) {
      if (m.state === "ok") continue;
      for (const e of m.entries) out.push({ month: m.month, entry: e });
    }
    return out;
  }, [months]);

  const frame = (body) => (embedded
    ? <div className="stack">{body}</div>
    : <main className="page page--wide">{body}</main>);

  if (loading) return frame(<Loading label="Reconciling prepaid balances…" />);
  if (error) return frame(<ErrorState message={error} onRetry={load} />);

  const agreed = months.filter((m) => m.state === "ok").length;
  const broken = months.filter((m) => m.state === "break");
  const open = months.filter((m) => m.state !== "ok");

  const posted = `${data.year} · workbook to ${monthLabel(data.latestScheduleMonth)}`
               + ` · ledger to ${monthLabel(data.latestLedgerMonth)}`;

  const inner = (
    <>
      {embedded ? (
        <div className="prepaid-subhead">
          <span className="muted">{posted}</span>
          <button className="btn btn--sm" onClick={load}>Refresh</button>
        </div>
      ) : (
        <PageHeader title="Prepaid expenses" subtitle={posted}>
          <button className="btn" onClick={load}>Refresh</button>
        </PageHeader>
      )}

      <div className="stack">
        <div className="stat-grid">
          <Stat label="Months agreed" accent="var(--pos)" value={String(agreed)}
                meta={`of ${months.filter((m) => m.state !== "pending").length} the workbook covers`} />
          <Stat label="Months out" accent={broken.length ? "var(--warn)" : "var(--s-2)"}
                value={String(broken.length)}
                meta={broken.length ? broken.map((m) => monthLabel(m.month)).join(", ") : "nothing to chase"} />
          <Stat label="Not yet in the workbook" accent="var(--s-3)"
                value={String(months.filter((m) => m.state === "pending").length)}
                meta="ledger has moved on; workbook has not" />
          <Stat label="Opening balance" accent="var(--s-4)" value={money(data.openingBalance)}
                meta={`carried in from ${monthLabel(`${data.year - 1}-12`)}`} />
        </div>

        <Card
          title="Closing balance, workbook against ledger"
          note="a month opens only where the two disagree; its entries then sit under that month"
          flush
        >
          <div className="tbl-wrap tbl-wrap--flush prepaid-wrap" ref={scrollRef}>
            <table className="tbl prepaid-tbl">
              <thead>
                <tr>
                  <th className="sticky-col prepaid-desc">&nbsp;</th>
                  {months.map((m) => (
                    <th key={m.month} className="num">{monthLabel(m.month)}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr className="prepaid-balance">
                  <th className="sticky-col">Closing per prepaid sheet</th>
                  {months.map((m) => (
                    <td key={m.month} className="num">{cash(m.scheduleBalance)}</td>
                  ))}
                </tr>

                {entryRows.map(({ month, entry }) => {
                  const { lead, rest } = describe(entry);
                  return (
                    <tr key={`${month}-${entry.id}`} className="prepaid-entry">
                      <td className="sticky-col prepaid-desc" title={entry.description || ""}>
                        <span className="prepaid-entry-lead">{lead}</span>
                        {rest && <span className="prepaid-entry-rest">{rest}</span>}
                        <small className="muted">
                          {[entry.tranType, entry.refNo, fmtDate(entry.date)].filter(Boolean).join(" · ")}
                        </small>
                      </td>
                      {months.map((c) => (
                        <td key={c.month} className={`num${Number(entry.amount) < 0 ? " neg" : ""}`}>
                          {c.month === month ? money(Number(entry.amount)) : null}
                        </td>
                      ))}
                    </tr>
                  );
                })}

                {!!open.length && (
                  <tr className="prepaid-subtotal">
                    <th className="sticky-col">Net ledger entries</th>
                    {months.map((m) => (
                      <td key={m.month} className={`num${m.glMovement < 0 ? " neg" : ""}`}>
                        {m.state === "ok" ? null : money(m.glMovement)}
                      </td>
                    ))}
                  </tr>
                )}

                <tr className="prepaid-balance prepaid-balance--gl">
                  <th className="sticky-col">Closing per general ledger</th>
                  {months.map((m) => (
                    <td key={m.month} className="num">{cash(m.glBalance)}</td>
                  ))}
                </tr>

                <tr className="prepaid-diff">
                  <th className="sticky-col">Difference</th>
                  {months.map((m) => (
                    <td key={m.month}
                        className={`num${m.state === "break" ? " warn-text" : ""}`}>
                      {m.state === "pending"
                        ? <span className="muted">not yet</span>
                        : m.state === "ok"
                          ? <span className="prepaid-tick" title="agreed">✓</span>
                          : money(m.difference)}
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        </Card>

        <p className="toolbar-note">
          Workbook: <strong>{data.scheduleSheet}</strong>, last saved {fmtDate(data.scheduleModifiedAt)}.
          A month with no prepaid-sheet balance is shown as <em>not yet</em> — the ledger has entries
          the workbook has not reached, which is expected rather than a break.
        </p>
      </div>
    </>
  );

  return embedded ? inner : <main className="page page--wide">{inner}</main>;
}
